import {Injectable} from '@angular/core';
import {NumberWithDecimalsFormatter} from './formatters/number.formatters';
import {BaselineToKilometersFormatter} from './formatters/baseline.formatters';
import {RaToHmsFormatter} from './formatters/ra.formatters';
import {DecToDmsFormatter} from './formatters/dec.formatters';
import {IntegrationToHoursFormatter, IntegrationToMinutesFormatter} from './formatters/integration.formatters';
import {VelocityResolutionToMetersPerSecondFormatter} from './formatters/velocityResolution.formatters';
import {
  FrequencyResolutionToGHZFormatter,
  FrequencyResolutionToMHZFormatter
} from './formatters/frecuency-resolution.formatters';
import {SensitivityToJyFormatter, SensitivityToMicroJyFormatter} from './formatters/sensitivity.formatters';
import {
  WaterVaporToAFormatter,
  WaterVaporToCmFormatter,
  WaterVaporToMFormatter,
  WaterVaporToNmFormatter,
  WaterVaporToUmFormatter
} from './formatters/waterVapor.formatters';
import {DecodedOusFormatter} from './formatters/ous.formatters';
import {
  AngleToArcMinutesFormatter,
  AngleToDegreesFormatter,
  AngleToMilliArcSecondsFormatter
} from './formatters/angle.formatters';
import {DefaultFormatter} from './formatters/default.formatters';
import {Formatter} from './formatters/units-formatter';
import {ColumnUnits} from './units-formatters.service';

const raDecNumberDecimals = 6;
const frequencyNumberDecimals = 4;
const observationsBaselineL80 = 'observationsBaselineL80';
const observationsBaselineL5 = 'observationsBaselineL5';
const observationsRa = 'observationsRa';
const observationsDec = 'observationsDec';
const observationsIntegration = 'observationsIntegration';
const observationsVelocityResolution = 'observationsVelocityResolution';
const observationsFrequencyResolution = 'observationsFrequencyResolution';
const observationsGalacticLongitude = 'observationsGalacticLongitude';
const observationsGalacticLatitude = 'observationsGalacticLatitude';
const observationsSpatialResolution = 'observationsSpatialResolution';
const observationsLineSensitivity = 'observationsLineSensitivity';
const observationsContinuumSensitivity = 'observationsContinuumSensitivity';
const observationsPwv = 'observationsPwv';
const observationsFov = 'observationsFov';
const observationsLargestAngularScale = 'observationsLargestAngularScale';
const observationsMous = 'observationsMous';

@Injectable({
  providedIn: 'root'
})
export class UnitsFormattersFactory {
  getFormatter(columnUnits?: ColumnUnits): Formatter {
    const {columnId, units} = columnUnits;

    switch (true) {
      // Baseline Max
      case columnId === observationsBaselineL80 && units === 'm':
        return new NumberWithDecimalsFormatter();
      case columnId === observationsBaselineL80 && units === 'km':
        return new BaselineToKilometersFormatter();

      // Baseline Min
      case columnId === observationsBaselineL5 && units === 'm':
        return new NumberWithDecimalsFormatter();
      case columnId === observationsBaselineL5 && units === 'km':
        return new BaselineToKilometersFormatter();

      // RA
      case columnId === observationsRa && units === 'h:m:s':
        return new RaToHmsFormatter();
      case columnId === observationsRa && units === 'deg':
        return new NumberWithDecimalsFormatter(raDecNumberDecimals);

      // Dec
      case columnId === observationsDec && units === 'd:m:s':
        return new DecToDmsFormatter();
      case columnId === observationsDec && units === 'deg':
        return new NumberWithDecimalsFormatter(raDecNumberDecimals);

      // Integration
      case columnId === observationsIntegration && units === 's':
        return new NumberWithDecimalsFormatter();
      case columnId === observationsIntegration && units === 'm':
        return new IntegrationToMinutesFormatter();
      case columnId === observationsIntegration && units === 'h':
        return new IntegrationToHoursFormatter();

      // Velocity Resolution
      case columnId === observationsVelocityResolution && units === 'm/s':
        return new VelocityResolutionToMetersPerSecondFormatter();
      case columnId === observationsVelocityResolution && units === 'km/s':
        return new NumberWithDecimalsFormatter();

      // Galactic Longitude
      case columnId === observationsGalacticLongitude:
        return new NumberWithDecimalsFormatter();

      // Galactic Latitude
      case columnId === observationsGalacticLatitude:
        return new NumberWithDecimalsFormatter();

      // Frequency Resolution
      case columnId === observationsFrequencyResolution && units === 'kHz':
        return new NumberWithDecimalsFormatter(frequencyNumberDecimals);
      case columnId === observationsFrequencyResolution && units === 'GHz':
        return new FrequencyResolutionToGHZFormatter(frequencyNumberDecimals);
      case columnId === observationsFrequencyResolution && units === 'MHz':
        return new FrequencyResolutionToMHZFormatter(frequencyNumberDecimals);

      // Line Sensitivity
      case columnId === observationsLineSensitivity && units === 'mJy/beam':
        return new NumberWithDecimalsFormatter();
      case columnId === observationsLineSensitivity && units === 'Jy/beam':
        return new SensitivityToJyFormatter();
      case columnId === observationsLineSensitivity && units === '&mu;Jy/beam':
        return new SensitivityToMicroJyFormatter();

      // Continuum Sensitivity
      case columnId === observationsContinuumSensitivity && units === 'mJy/beam':
        return new NumberWithDecimalsFormatter(4);
      case columnId === observationsContinuumSensitivity && units === 'Jy/beam':
        return new SensitivityToJyFormatter(4);
      case columnId === observationsContinuumSensitivity && units === '&mu;Jy/beam':
        return new SensitivityToMicroJyFormatter(4);

      // Precipitable Water Vapour
      case columnId === observationsPwv && units === 'm':
        return new WaterVaporToMFormatter();
      case columnId === observationsPwv && units === 'cm':
        return new WaterVaporToCmFormatter();
      case columnId === observationsPwv && units === 'um':
        return new WaterVaporToUmFormatter();
      case columnId === observationsPwv && units === 'nm':
        return new WaterVaporToNmFormatter();
      case columnId === observationsPwv && units === 'a':
        return new WaterVaporToAFormatter();
      case columnId === observationsPwv && units === 'mm':
        return new NumberWithDecimalsFormatter();

      // Field Of View
      case columnId === observationsFov && units === 'arcsec':
        return new NumberWithDecimalsFormatter();
      case columnId === observationsFov && units === 'deg':
        return new AngleToDegreesFormatter();
      case columnId === observationsFov && units === 'arcmin':
        return new AngleToArcMinutesFormatter();
      case columnId === observationsFov && units === 'miliarsec':
        return new AngleToMilliArcSecondsFormatter();

      // Largest Angular Scale
      case columnId === observationsLargestAngularScale && units === 'arcsec':
        return new NumberWithDecimalsFormatter();
      case columnId === observationsLargestAngularScale && units === 'deg':
        return new AngleToDegreesFormatter();
      case columnId === observationsLargestAngularScale && units === 'arcmin':
        return new AngleToArcMinutesFormatter();
      case columnId === observationsLargestAngularScale && units === 'miliarsec':
        return new AngleToMilliArcSecondsFormatter();

      // Angular Resolution
      case columnId === observationsSpatialResolution && units === 'arcsec':
        return new NumberWithDecimalsFormatter();
      case columnId === observationsSpatialResolution && units === 'deg':
        return new AngleToDegreesFormatter();
      case columnId === observationsSpatialResolution && units === 'arcmin':
        return new AngleToArcMinutesFormatter();
      case columnId === observationsSpatialResolution && units === 'miliarsec':
        return new AngleToMilliArcSecondsFormatter();

      // MOUS
      case columnId === observationsMous:
        return new DecodedOusFormatter();

      // Default
      default:
        return new DefaultFormatter();
    }
  }
}
